import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';

// Pages
import Login from './pages/Login';
import ChangePassword from './pages/ChangePassword';
import Dashboard from './pages/Dashboard';
import RecipeList from './pages/RecipeList';
import RecipeDetail from './pages/RecipeDetail';
import RecipeForm from './pages/RecipeForm';
import AdminUsers from './pages/AdminUsers';
import UnitConverter from './pages/UnitConverter';

// Components
import Navbar from './components/Navbar';
import LoadingSpinner from './components/LoadingSpinner';

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { isAuthenticated, loading, user, mustChangePassword } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Force password change if required (except on change-password page)
  if (mustChangePassword && window.location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  if (adminOnly && user?.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Public Route (redirect if already logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, mustChangePassword } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (isAuthenticated) {
    if (mustChangePassword) {
      return <Navigate to="/change-password" replace />;
    }
    return <Navigate to="/" replace />;
  }

  return children;
};

// Layout with Navbar
const Layout = ({ children }) => {
  const { mustChangePassword } = useAuth();
  
  return (
    <div className="min-h-screen bg-gray-100">
      {!mustChangePassword && <Navbar />}
      <main className={mustChangePassword ? '' : 'pt-4'}>
        {children}
      </main>
    </div>
  );
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        } 
      />

      {/* Change Password (special - authenticated but restricted) */}
      <Route 
        path="/change-password" 
        element={
          <ProtectedRoute>
            <ChangePassword />
          </ProtectedRoute>
        } 
      />

      {/* Protected Routes */}
      <Route 
        path="/" 
        element={
          <ProtectedRoute>
            <Layout>
              <Dashboard />
            </Layout>
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/recipes" 
        element={
          <ProtectedRoute>
            <Layout>
              <RecipeList />
            </Layout>
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/recipes/new" 
        element={
          <ProtectedRoute>
            <Layout>
              <RecipeForm />
            </Layout>
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/recipes/:id" 
        element={
          <ProtectedRoute>
            <Layout>
              <RecipeDetail />
            </Layout>
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/recipes/:id/edit" 
        element={
          <ProtectedRoute>
            <Layout>
              <RecipeForm />
            </Layout>
          </ProtectedRoute>
        } 
      />

      {/* Admin Routes */}
      <Route 
        path="/admin/users" 
        element={
          <ProtectedRoute adminOnly>
            <Layout>
              <AdminUsers />
            </Layout>
          </ProtectedRoute>
        } 
      />

      {/* Tools */}
      <Route 
        path="/tools/converter" 
        element={
          <ProtectedRoute>
            <Layout>
              <UnitConverter />
            </Layout>
          </ProtectedRoute>
        } 
      />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
